import { ButtonIcon, DatePicker, FormFeedback } from '@morressier/alembic';
import { Box, Flex } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import {
  get,
  FieldError as IFieldError,
  useFormContext
} from 'react-hook-form';
import { FormFieldDateInput } from '@publishing-engine/domain/types/article';
import FormFieldErrorMessage from '../FormField/common/FormFieldErrorMessage';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

interface IFormFieldDateProps {
  config: FormFieldDateInput;
  isEditingAllowed?: boolean;
  errorMessage?: string;
  dependentFieldPath?: string;
  dependentFieldConfig?: FormFieldDateInput;
  dependentFieldValue?: string;
  requiredWithFieldPath?: string;
}

const CustomFormFieldDateValidator: React.FC<IFormFieldDateProps> = ({
  config,
  isEditingAllowed = true,
  errorMessage,
  dependentFieldPath,
  dependentFieldConfig,
  dependentFieldValue,
  requiredWithFieldPath
}) => {
  const {
    params: { validations, labels, externalFieldDependency },
    caseDataFields: { main: fieldPath }
  } = config;

  const {
    formState: { errors },
    register,
    watch,
    setError,
    clearErrors,
    setValue
  } = useFormContext();

  const dependentValue = dependentFieldPath
    ? watch(dependentFieldPath)
    : validations.min;
  const requiredWithValue = requiredWithFieldPath
    ? watch(requiredWithFieldPath)
    : '';

  const value = watch(fieldPath) as string;
  const hasValue = Boolean(value && `${value}`.trim());
  const hasRequiredWithValue = Boolean(
    requiredWithValue && `${requiredWithValue}`.trim()
  );
  const hasDependentValue = Boolean(
    dependentValue && `${dependentValue}`.trim()
  );

  const error = get(errors, fieldPath) as IFieldError;

  useEffect(() => {
    if (externalFieldDependency?.value !== dependentFieldValue) {
      if (get(errors, fieldPath)?.type === 'custom') {
        clearErrors(fieldPath);
      }
      return;
    }

    if (requiredWithFieldPath && hasRequiredWithValue && !hasValue) {
      setError(fieldPath, {
        type: 'custom',
        message: `${labels.main} is required.`
      });
      return;
    }

    if (
      dependentFieldPath &&
      hasDependentValue &&
      hasValue &&
      new Date(dependentValue).getTime() > new Date(value).getTime()
    ) {
      setError(fieldPath, {
        type: 'custom',
        message: `${labels.main} date must be greater than ${dependentFieldConfig?.params?.labels?.main} date`
      });
    } else {
      // Clear the custom error if condition no longer matches
      if (get(errors, fieldPath)?.type === 'custom') {
        clearErrors(fieldPath);
      }
    }
  }, [
    clearErrors,
    dependentFieldPath,
    dependentFieldValue,
    dependentFieldConfig,
    dependentValue,
    errors,
    externalFieldDependency?.value,
    fieldPath,
    labels.main,
    requiredWithFieldPath,
    hasRequiredWithValue,
    hasDependentValue,
    hasValue,
    setError,
    value
  ]);

  if (externalFieldDependency?.value !== dependentFieldValue) return null;

  return (
    <Flex flexDirection="column" gap={2} alignItems="flex-start">
      <FieldLabel
        label={labels.main}
        subText={labels.subtitle}
        isRequired={validations.required}
      />
      <Flex flexDirection="column" flex={1} width={'100%'} gap={2}>
        <Box width="100%" position="relative">
          <Box>
            <DatePicker
              value={value}
              {...register(fieldPath, {
                validate: val => {
                  const hasInputValue = Boolean(val && val.trim() !== '');

                  if (validations.required && !hasInputValue) {
                    return `${labels.main} date is required.`;
                  }
                  if (
                    requiredWithFieldPath &&
                    hasRequiredWithValue &&
                    !hasInputValue
                  ) {
                    return `${labels.main} date is required.`;
                  }
                  if (
                    dependentValue &&
                    hasInputValue &&
                    new Date(dependentValue).getTime() > new Date(val).getTime()
                  ) {
                    return `${labels.main} date must be greater than ${dependentFieldConfig?.params?.labels?.main} date`;
                  }
                  return true;
                }
              })}
              isInvalid={Boolean(error || errorMessage)}
              min={dependentValue}
              isReadOnly={!isEditingAllowed}
              isDisabled={!isEditingAllowed}
            />
          </Box>
          {isEditingAllowed && hasValue ? (
            <Box
              position="absolute"
              right="2rem"
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
            >
              <ButtonIcon
                ariaLabel={`Clear ${labels.main}`}
                icon={<FontAwesomeIcon icon={faXmark} />}
                size="small"
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  setValue(fieldPath, '', {
                    shouldDirty: true,
                    shouldValidate: true
                  });
                }}
              />
            </Box>
          ) : null}
        </Box>

        {errorMessage ? (
          <FormFeedback variant="error">{errorMessage}</FormFeedback>
        ) : (
          <FormFieldErrorMessage config={config} />
        )}
      </Flex>
    </Flex>
  );
};

export default CustomFormFieldDateValidator;
